import './today.css';
import { IonContent, IonToast } from '@ionic/react';
import { useMemo, useState } from 'react';
import { formatDate } from '../i18n/format';
import { useMessages } from '../i18n/LanguageProvider';
import { InstallHint } from '../install/InstallHint';
import { PageHeader } from '../PageHeader';
import { useOpenSettings } from '../settings/SettingsProvider';
import { useAppData } from '../state/AppDataProvider';
import { DinnerCard } from './DinnerCard';
import { NoEnergySheet } from './NoEnergySheet';
import { PlanPrompt } from '../plan/PlanPrompt';
import { RecipeSheet } from './RecipeSheet';
import { ShoppingCard } from './ShoppingCard';
import { recipeById } from '../recipes/recipe-lookup';
import { todaySummary, type TodaySummary } from './today-summary';
import { TomorrowCard } from './TomorrowCard';

type Sheet = 'recipe' | 'no-energy' | undefined;

function Hints({ summary }: { summary: TodaySummary }) {
  const { t } = useMessages();
  const openSettings = useOpenSettings();
  const holiday = summary.holidayTomorrow;
  return (
    <>
      {summary.askFederalState && (
        <button type="button" className="app-hint" onClick={openSettings}>
          {t.chooseFederalState}
        </button>
      )}
      {holiday && <p className="app-hint">{t.holidayTomorrow(t.holidays[holiday] ?? holiday)}</p>}
    </>
  );
}

/**
 * DE: Bildschirm „Heute“ (Variante A): Einkauf, Abendessen mit Zeit, morgen.
 * EN: Today screen (variant A): shopping, dinner with its time, tomorrow.
 */
export function TodayPage() {
  const { data, today } = useAppData();
  const { language, t } = useMessages();
  const summary = useMemo(() => todaySummary(data, today), [data, today]);
  const [sheet, setSheet] = useState<Sheet>();
  const [notice, setNotice] = useState<string>();
  const close = () => setSheet(undefined);
  const { dinner, tomorrow } = summary;
  return (
    <>
      <PageHeader title={t.tabToday} date={formatDate(today, language, 'weekday-long')} />
      <IonContent>
        <div className="app-page">
          <InstallHint />
          <Hints summary={summary} />
          {summary.needsPlan ? (
            <PlanPrompt mode="first" />
          ) : (
            <ShoppingCard shopping={summary.shopping} />
          )}
          {dinner && (
            <DinnerCard
              dinner={dinner}
              members={data.household.members}
              onRecipe={() => setSheet('recipe')}
              onNoEnergy={() => setSheet('no-energy')}
            />
          )}
          {tomorrow && <TomorrowCard tomorrow={tomorrow} />}
          {summary.offerNextPlan && <PlanPrompt mode="next" />}
        </div>
        {dinner && (
          <RecipeSheet
            recipe={recipeById(dinner.recipeId)}
            servings={dinner.servings}
            isOpen={sheet === 'recipe'}
            onClose={close}
          />
        )}
        <NoEnergySheet isOpen={sheet === 'no-energy'} onClose={close} onNotice={setNotice} />
        <IonToast
          isOpen={notice !== undefined}
          message={notice ?? ''}
          duration={5000}
          onDidDismiss={() => setNotice(undefined)}
        />
      </IonContent>
    </>
  );
}
